/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Network } from '@capacitor/network';
import { useMutation } from '@tanstack/react-query';
import { LD, useLDFlag } from '@dutchie-pos/utils';
import React from 'react';

import { PaymentType, PrepaymentType } from 'src/models/checkout';
import type { PaymentMethod } from 'src/models/checkout';
import { useAppSelector } from 'src/utils';
import { HttpApiError, post } from 'src/utils/helpers/http';
import { useBaseAPIParams } from 'src/utils/hooks/useBaseAPIParams';

import { useActiveCustomer } from './use-carts';

const CHECKOUT_URL = '/v2/guest/checkOut_Guest';

export type useCheckoutType = {
  onError?: (ex: any) => void;
  onSuccess?: (data: CheckoutResponse) => void;
};

export type CheckoutRequest = {
  CCPaid: number;
  CheckPaid?: number;
  DebitPaid: number;
  DueCustomer: number;
  GiftPaid: number;
  OrderId: number;
  Paidamt: number;
  // Manual payment entries only - Rethink/Dutchiepay entries are still derived below from
  // PrepaymentType, not passed in here, to avoid duplicating that existing logic.
  PaymentMethods?: PaymentMethod[];
  PreAuth: number;
  PrepaymentType: string;
  SubTotal: number;
  TaxAmt: number;
  TotalCharges: number;
};

export type CheckoutResponse = {
  OverThreshold: boolean;
  ReesultTxt: string;
  ResultId: number;
};

export type DutchiePayIntegrationData = {
  ConsumerMessage?: string;
  ErrorCode?: string;
  Message?: string;
  Metadata?: Record<string, string>;
  PaymentError?: string;
  PosMessage?: string;
};

export class CheckoutError extends Error {
  consumerMessage?: string;
  errorCode?: string;
  metadata?: Record<string, string>;
  paymentError?: string;
  posMessage?: string;

  constructor(message: string, integrationData?: DutchiePayIntegrationData) {
    super(message);
    this.name = 'CheckoutError';
    this.paymentError = integrationData?.PaymentError;
    this.errorCode = integrationData?.ErrorCode;
    this.consumerMessage = integrationData?.ConsumerMessage;
    this.posMessage = integrationData?.PosMessage;
    this.metadata = integrationData?.Metadata;
  }
}

export const isDutchiePayCheckoutError = (ex: unknown, prepaymentType?: string): boolean =>
  (ex instanceof CheckoutError && ex.paymentError === PrepaymentType.Dutchiepay) ||
  prepaymentType === PrepaymentType.Dutchiepay;

export function useCheckout({ onError, onSuccess }: useCheckoutType) {
  const isDutchiePayDeclineMessagingEnabled = useLDFlag(
    LD.FINTECH_MOBILE_CHECKOUT_ERROR_MESSAGING_UPDATES,
    false,
  );
  const activeCart = useAppSelector((state) => state.mobileCheckout.activeCart);
  const selectedRegisterId = useAppSelector((state) => state.user.selectedRegister?.id);
  const { data: activeCustomer } = useActiveCustomer();

  const baseAPIParams = useBaseAPIParams();

  return useMutation({
    mutationKey: ['checkout'],
    mutationFn: async (Request: CheckoutRequest) => {
      try {
        const resp = await post<CheckoutResponse[]>(CHECKOUT_URL, {
          ...baseAPIParams,
          ...Request,
          PaymentMethods: [
            ...(Request.PaymentMethods ?? []),
            ...(Request.PrepaymentType === PrepaymentType.Rethink
              ? [
                  {
                    amount: Request.CCPaid,
                    finalized: true,
                    id: activeCart?.PreauthInfo?.PreauthId,
                    name: PrepaymentType.Rethink,
                    tipAmount: activeCart?.TipAmount ?? 0,
                    type: PaymentType.RethinkEcomm,
                  },
                ]
              : []),
            ...(Request.PrepaymentType === PrepaymentType.Dutchiepay
              ? [
                  {
                    amount: Request.PreAuth,
                    id: activeCart?.PreauthInfo?.PreauthId,
                    name: PrepaymentType.Dutchiepay,
                    tipAmount: activeCart?.TipAmount ?? 0,
                    type: PaymentType.DutchiePay,
                  },
                ]
              : []),
          ],
          ReactVersion: React.version,
          Data: activeCart?.PreauthInfo,
          TerminalId: selectedRegisterId,
          TransType: 'Cash',
          ShipmentId: activeCustomer?.ShipmentId,
          LoyaltyPoints: activeCart?.Loyalty?.AppliedLoyaltyPoints ?? 0,
          DiscountAmt: activeCart?.TotalDiscount,
          TipPaid: activeCart?.TipAmount ?? 0,
          TotalItems: activeCart?.TotalItems,
        }, { integrationErrorMessage: true, preserveIntegrationData: isDutchiePayDeclineMessagingEnabled });

        if (resp) {
          const data = resp ?? [];
          if (data.length > 0) {
            return data[0];
          }

          return await Promise.reject(new Error(`An error occurred. Please try again.`));
        } else {
          return Promise.reject(new Error(`An error occurred. Please try again.`));
        }
      } catch (ex: unknown) {
        const connectionStataus = await Network.getStatus();
        if (!connectionStataus?.connected) {
          throw new Error('No internet connection found.');
        }

        if (isDutchiePayDeclineMessagingEnabled) {
          if (ex instanceof HttpApiError) {
            const integrationData = ex.integrationData as DutchiePayIntegrationData | undefined;
            throw new CheckoutError(ex.message, integrationData);
          }
          if (ex instanceof Error) {
            throw ex;
          }
          throw new Error(String(ex));
        }

        if (ex instanceof Error && ex?.message) {
          try {
            const errorObject = JSON.parse(ex.message);
            throw new Error(`${String(errorObject.Message ?? '')} ${String(errorObject.IntegrationMessage ?? '')}`);
          } catch (parseError) {
            if (parseError instanceof SyntaxError) {
              throw new Error(ex.message);
            }
            throw parseError;
          }
        }

        if (typeof ex === 'string') {
          throw new Error(ex);
        }

        throw ex;
      }
    },
    onError,
    onSuccess,
  });
}
